import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Grid, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import idCardImage from 'features/pdf/intelliCheck/images/id-card.png';
import user from 'features/pdf/intelliCheck/images/user.png';
import React from 'react';

type ScanImagesProps = {
  front: string | null;
  back: string | null;
  selfie: string | null;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullWidth: {
      width: '100%',
      display: 'flex',
      paddingTop: theme.spacing(3),
      marginBottom: theme.spacing(9),
    },
    idFrame: {
      width: 240,
      height: 137,
      gap: theme.spacing(8),
    },
    selfieFrame: {
      width: 200,
      height: 137,
      gap: theme.spacing(8),
    },
    id: {
      height: 250,
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      padding: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    emptyIdCard: {
      width: 160,
      height: 80,
      flexDirection: 'column',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptySelfie: {
      width: 60,
      height: 70,
      flexDirection: 'column',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptyIdCardText: {
      fontSize: pxToRem(10),
      fontWeight: 350,
      color: theme.palette.text.primary,
      lineHeight: '100%',
      textAlign: 'center',
      marginTop: theme.spacing(1),
    },
    imagesText: {
      fontSize: pxToRem(12),
      fontWeight: 500,
      color: theme.palette.text.primary,
      textAlign: 'center',
    },
    idImage: {
      width: 200,
      height: 200,
      borderRadius: 3.5,
      objectFit: 'contain',
    },
    idCardIcon: {
      width: 110,
      height: 140,
      objectFit: 'cover',
      borderRadius: 3.5,
    },
    userIcon: {
      width: 54,
      height: 70,
      objectFit: 'cover',
      borderRadius: 3.5,
    },
  }),
);

type ImageConfig = {
  label: string;
  value: string | null;
  frameClass: string;
  emptyClass: string;
  emptyIcon: string;
  emptyAlt: string;
  imageClass: string;
  emptyText: string;
  alt: string;
};

const ScanImages: React.FC<ScanImagesProps> = ({ front, back, selfie }) => {
  const classes = useStyles();

  const images: ImageConfig[] = [
    {
      label: 'Front',
      value: front,
      frameClass: classes.idFrame,
      emptyClass: classes.emptyIdCard,
      emptyIcon: idCardImage,
      emptyAlt: 'Front ID',
      imageClass: classes.idImage,
      emptyText: 'Picture Not Available',
      alt: 'Front ID',
    },
    {
      label: 'Back',
      value: back,
      frameClass: classes.idFrame,
      emptyClass: classes.emptyIdCard,
      emptyIcon: idCardImage,
      emptyAlt: 'Back ID',
      imageClass: classes.idImage,
      emptyText: 'Picture Not Available',
      alt: 'Back ID',
    },
    {
      label: 'Selfie',
      value: selfie,
      frameClass: classes.idFrame,
      emptyClass: classes.emptyIdCard,
      emptyIcon: user,
      emptyAlt: 'Selfie',
      imageClass: classes.idImage,
      emptyText: 'Picture Not Available',
      alt: 'Selfie',
    },
  ];

  const getImageSrc = (value: string | null) => (value ? `data:image/jpeg;base64,${value}` : undefined);

  return (
    <div className={classes.fullWidth}>
      <Grid container>
        {images.map((img) => (
          <Grid item xs={4} key={img.label} spacing={2}>
            <div className={img.frameClass}>
              <div className={classes.id}>
                {img.value ? (
                  <img src={getImageSrc(img.value)} alt={img.alt} className={img.imageClass} />
                ) : (
                  <div className={img.emptyClass}>
                    <img
                      src={img.emptyIcon}
                      alt={img.emptyAlt}
                      className={img.label === 'Selfie' ? classes.userIcon : classes.idCardIcon}
                    />
                    <Typography className={classes.emptyIdCardText}>{img.emptyText}</Typography>
                  </div>
                )}
              </div>
              <Typography className={classes.imagesText}>{img.label}</Typography>
            </div>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default ScanImages;
